import { createContext, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotify } from '@/components/snackbar';
import {
  createAccountPF,
  createAccountPJ,
  getAccount,
  getAccountList,
} from '@/services/baas';
import { ACCOUNT_CACHE_TIME } from '@/utils/baas/constants';
import { toSafeString } from '@/utils/baas/formatters';
import { useSessionContext } from '@/auth/useSessionContext';

export const BaasAccountContext = createContext({
  account: null,
  accountResolutionStatus: 'idle',
  accountResolutionError: null,
  isLoadingAccount: false,
  isFetchingAccount: false,
  isCreatingPF: false,
  isCreatingPJ: false,
  createPFAccount: async () => {},
  createPJAccount: async () => {},
  refreshAccount: async () => {},
  accountUpdatedAt: 0,
  hasAccount: false,
  isDocumentLocked: false,
  isWithdrawalBlocked: true,
  statusError: null,
});

const defaultAccount = {
  id: null,
  account_id: null,
  default_pix_key: null,
  type: null,
  documentNumber: null,
  documentType: null,
  celcoin_account_id: null,
  nominal_withdrawal_registered: false,
  status: null,
  proposal_status: null,
  partner: null,
  agency: null,
  account_number: null,
  businessName: null,
  tradingName: null,
  documentscopy_url: null,
  can_continue_verification: false,
  proposal_valid_until: null,
  proposal_is_expired: null,
  rejected_reason: null,
  provider_error_message: null,
  should_use_new_pj_kyc: false,
  new_pj_kyc_start_date: null,
  has_pin_defined: null,
  has_operation_password: null,
  operation_password_created: null,
  created_at: null,
};

const normalizeBoolean = (value) => value === true || value === 'true' || value === 1 || value === '1';

// Preserva a ausência: quem consome distingue "o backend disse não" de "não
// respondeu" para decidir entre a API e o fallback local.
const normalizeOptionalBoolean = (value) =>
  value === undefined || value === null ? undefined : normalizeBoolean(value);

const normalizeRolloutDecision = (payload = {}) => ({
  should_use_new_pj_kyc: normalizeBoolean(payload.should_use_new_pj_kyc),
  new_pj_kyc_start_date: payload.new_pj_kyc_start_date || null,
});

const normalizeOperationPasswordStatus = (payload = {}) => {
  const value = [
    payload.has_pin_defined,
    payload.hasPinDefined,
    payload.has_operation_password,
    payload.hasOperationPassword,
    payload.operation_password_created,
    payload.operationPasswordCreated,
    payload.has_banking_password,
    payload.hasBankingPassword,
    payload.banking_password_created,
    payload.bankingPasswordCreated,
    payload.has_account_pin,
    payload.hasAccountPin,
    payload.account_pin_created,
    payload.accountPinCreated,
  ].find((item) => item !== undefined && item !== null);

  return value === undefined ? null : normalizeBoolean(value);
};

/**
 * @param {string} proposalStatus - Status da proposta 
 * @returns {string} Status mapeado
 */
const mapProposalStatus = (proposalStatus) => {
  if (!proposalStatus) return null;

  const normalized = typeof proposalStatus === 'string'
    ? proposalStatus
    : toSafeString(proposalStatus);

  if (!normalized) return null;

  const statusMap = {
    pending: 'pending',
    pending_backgroundcheck: 'pending',
    approved_backgroundcheck: 'pending',
    reproved_backgroundcheck: 'error',
    pending_documentscopy: 'pending',
    approved_documentscopy: 'pending',
    reproved_documentscopy: 'error',
    processing_documentscopy: 'pending',
    approved_proposal: 'pending',
    reproved_proposal: 'error',
    account_created: 'created',
    account_error: 'error',
    canceled: 'canceled',
    cancelled: 'canceled',
  };

  return statusMap[normalized] || 'pending';
};

const getSafeString = (value) => {
  if (value === null || value === undefined || value === '') return null;
  return toSafeString(value);
};

const normalizeProposalStatus = (payload = {}) => {
  const rawStatus = payload.proposal_status ?? payload.status;

  if (rawStatus !== null && rawStatus !== undefined) {
    return toSafeString(rawStatus).toLowerCase();
  }

  if (payload.is_active === true) {
    return 'account_created';
  }

  if (payload.is_active === false && (payload.document_number || payload.account_number)) {
    return 'canceled';
  }

  return null;
};

const normalizeDocumentNumber = (payload = {}) =>
  getSafeString(payload.document_number || payload.documentNumber);

const getDocumentTypeFromPayload = (payload = {}, documentNumber) => {
  if (payload.documentType) return payload.documentType;
  if (payload.account_type === 'PJ') return 'CNPJ';
  if (payload.account_type === 'PF') return 'CPF';
  if (documentNumber?.length === 14) return 'CNPJ';
  if (documentNumber?.length === 11) return 'CPF';
  return null;
};

const normalizeAccountData = (payload = {}) => {
  if (!payload || typeof payload !== 'object' || payload.detail) {
    return defaultAccount;
  }

  const proposalStatus = normalizeProposalStatus(payload);
  const documentNumberSafe = normalizeDocumentNumber(payload);
  const documentType = getDocumentTypeFromPayload(payload, documentNumberSafe);
  let accountType = null;
  if (documentType === 'CNPJ') {
    accountType = 'company';
  }
  if (documentType === 'CPF') {
    accountType = 'individual';
  }
  const accountNumber = getSafeString(payload.account_number || payload.celcoin_account_id);

  return {
    ...defaultAccount,
    id: payload.id ?? payload.account_id ?? payload.accountId ?? null,
    account_id: payload.account_id ?? payload.accountId ?? payload.id ?? null,
    default_pix_key: getSafeString(payload.default_pix_key || payload.defaultPixKey),
    type: accountType,
    documentNumber: documentNumberSafe,
    documentType,
    celcoin_account_id: accountNumber,
    account_number: accountNumber,
    status: mapProposalStatus(proposalStatus),
    proposal_status: proposalStatus || null,
    nominal_withdrawal_registered: proposalStatus === 'account_created',
    businessName: payload.business_name || payload.businessName || payload.full_name || null,
    tradingName: payload.trading_name || payload.tradingName || payload.display_name || null,
    agency: payload.agency || null,
    documentscopy_url: payload.documentscopy_url || null,
    can_continue_verification: normalizeBoolean(payload.can_continue_verification),
    proposal_valid_until: payload.proposal_valid_until || null,
    proposal_is_expired:
      payload.proposal_is_expired === undefined
        ? null
        : normalizeBoolean(payload.proposal_is_expired),
    rejected_reason: payload.rejected_reason || null,
    provider_error_message: payload.provider_error_message || null,
    // Este objeto é montado campo a campo: o que não estiver aqui some.
    can_retry_account_opening: normalizeOptionalBoolean(payload.can_retry_account_opening),
    rejection_reason_is_displayable: normalizeOptionalBoolean(
      payload.rejection_reason_is_displayable
    ),
    retry_manually_allowed: normalizeOptionalBoolean(payload.retry_manually_allowed),
    ...normalizeRolloutDecision(payload),
    has_pin_defined: normalizeOperationPasswordStatus(payload),
    has_operation_password: normalizeOperationPasswordStatus(payload),
    operation_password_created: normalizeOperationPasswordStatus(payload),
    created_at: payload.created_at || payload.createdAt || null,
  };
};

const hasAccountData = (account) =>
  Boolean(account?.documentNumber || account?.account_number || account?.proposal_status);

const getStatusPriority = (account) => {
  if (account?.proposal_status === 'account_created') return 1;
  if (account?.proposal_status && account?.status === 'pending') return 2;
  if (account?.proposal_status && account?.status === 'error') return 3;
  if (account?.proposal_status) return 4;
  return 5;
};

const normalizeAccountFromList = (account = {}, payload = {}) => normalizeAccountData({
  ...account,
  should_use_new_pj_kyc:
    payload.should_use_new_pj_kyc ?? account.should_use_new_pj_kyc,
  new_pj_kyc_start_date:
    payload.new_pj_kyc_start_date ?? account.new_pj_kyc_start_date,
});

const getPreferredAccountFromList = (payload = {}) => {
  const accounts = Array.isArray(payload?.accounts) ? payload.accounts : [];

  return accounts
    .map((account) => normalizeAccountFromList(account, payload))
    .filter(hasAccountData)
    .sort((first, second) => getStatusPriority(first) - getStatusPriority(second))[0]
    || normalizeAccountFromList({}, payload);
};

const getMatchingAccountFromList = (payload = {}, account = {}) => {
  const accounts = Array.isArray(payload?.accounts) ? payload.accounts : [];
  const normalizedAccounts = accounts
    .map((listAccount) => normalizeAccountFromList(listAccount, payload))
    .filter(hasAccountData);
  const accountIdentifiers = [
    account?.id,
    account?.account_id,
    account?.account_number,
    account?.celcoin_account_id,
    account?.documentNumber,
    account?.document_number,
  ].filter(Boolean).map(toSafeString);

  const matchingAccount = normalizedAccounts.find((candidate) =>
    [
      candidate.id,
      candidate.account_id,
      candidate.account_number,
      candidate.celcoin_account_id,
      candidate.documentNumber,
    ]
      .filter(Boolean)
      .map(toSafeString)
      .some((identifier) => accountIdentifiers.includes(identifier))
  );

  const singleListAccount = normalizedAccounts.length === 1 ? normalizedAccounts[0] : undefined;
  const shouldPreferSingleUncreatedAccount =
    account?.proposal_status === 'account_created' &&
    singleListAccount?.proposal_status &&
    singleListAccount.proposal_status !== 'account_created';

  // O endpoint individual pode ficar momentaneamente defasado após a abertura
  // e ainda responder `account_created`, enquanto a lista já contém a proposta
  // atual em análise. Nesse caso restrito, a única conta da lista representa o
  // estado mais recente mesmo que os endpoints tragam identificadores distintos.
  return matchingAccount || (shouldPreferSingleUncreatedAccount ? singleListAccount : undefined);
};

const isNotFoundError = (error) => error?.response?.status === 404;

const getAccountFromListFallback = async () => {
  try {
    const accountListData = await getAccountList();
    return getPreferredAccountFromList(accountListData);
  } catch (error) {
    if (isNotFoundError(error)) return defaultAccount;
    throw error;
  }
};

const hydrateAccountFromList = async (mappedAccount) => {
  try {
    const accountListData = await getAccountList();
    const listAccount = getMatchingAccountFromList(accountListData, mappedAccount);
    if (!listAccount) return mappedAccount;

    const availableListData = Object.fromEntries(
      Object.entries(listAccount).filter(([, value]) => value !== null && value !== undefined)
    );
    return { ...mappedAccount, ...availableListData };
  } catch {
    return mappedAccount;
  }
};

export const resolveBaasAccount = async () => {
  try {
    const accountData = await getAccount();

    if (accountData?.detail) return getAccountFromListFallback();

    const mappedAccount = normalizeAccountData(accountData);
    return hasAccountData(mappedAccount)
      ? hydrateAccountFromList(mappedAccount)
      : getAccountFromListFallback();
  } catch (getAccountError) {
    if (!isNotFoundError(getAccountError)) throw getAccountError;
  }

  // O plano B era `get-account-status`, uma das rotas que a Conta Digital encerrada
  // responde com 410 (CBK-550). Como só o 404 era tratado, a 410 fazia a resolução
  // estourar. Sem ela, a conta que não existe segue direto para a lista, que é o que
  // já acontecia quando o status dava 404.
  return getAccountFromListFallback();
};

const getAccountResolutionErrorMessage = (error) => {
  if (error?.response?.status === 401) {
    return 'Sessão expirada. Por favor, faça login novamente.';
  }

  const responseMessage = error?.response?.data?.detail || error?.response?.data?.message;
  if (typeof responseMessage === 'string' && responseMessage.trim() && !responseMessage.trim().startsWith('<')) {
    return responseMessage;
  }

  return 'Não foi possível carregar a conta digital. Tente novamente.';
};

/**
 * Provider do contexto de conta BAAS
 * Gerencia estado da conta bancária, criação e atualização
 */
const BaasAccountProvider = ({ children }) => {
  const { user } = useSessionContext();

  const shouldFetchAccount = Boolean(user);

  const queryClient = useQueryClient();
  const notify = useNotify();

  const [statusError, setStatusError] = useState(null);

  const {
    data: account = defaultAccount,
    isLoading: isLoadingAccount,
    isFetching: isFetchingAccount,
    isError: hasAccountResolutionError,
    error: accountResolutionError,
    refetch: refetchAccount,
    dataUpdatedAt: accountUpdatedAt,
  } = useQuery({
    queryKey: ['baas.account'],
    queryFn: resolveBaasAccount,
    staleTime: ACCOUNT_CACHE_TIME,
    enabled: shouldFetchAccount,
  });

  let accountResolutionStatus = 'resolved';
  if (!shouldFetchAccount) accountResolutionStatus = 'idle';
  else if (isLoadingAccount && !accountUpdatedAt) accountResolutionStatus = 'loading';
  else if (hasAccountResolutionError) accountResolutionStatus = 'error';
  const accountResolutionErrorMessage = hasAccountResolutionError
    ? getAccountResolutionErrorMessage(accountResolutionError)
    : null;

  const refreshAccount = useCallback(async () => {
    setStatusError(null);
    return refetchAccount();
  }, [refetchAccount]);

  const normalizeAccountPayload = (payload = {}) => {
    if (!payload || typeof payload !== 'object') return {};

    const normalizedAccount = normalizeAccountData(payload);
    const safePayload = {
      ...payload,
      ...Object.fromEntries(
        Object.entries(normalizedAccount).filter(([, value]) => value !== null && value !== undefined)
      ),
    };

    if ('proposal_status' in safePayload && safePayload.proposal_status != null) {
      safePayload.proposal_status = toSafeString(safePayload.proposal_status).toLowerCase();
    }

    const stringifyIfPresent = (key) => {
      if (key in safePayload && safePayload[key] != null && typeof safePayload[key] !== 'string') {
        safePayload[key] = toSafeString(safePayload[key]);
      }
    };

    [
      'documentNumber',
      'document_number',
      'account_number',
      'celcoin_account_id',
      'agency',
      'business_name',
      'businessName',
      'trading_name',
      'tradingName',
      'documentscopy_url',
      'contactNumber',
      'businessEmail',
      'created_at',
    ].forEach(stringifyIfPresent);

    if (safePayload.document_number && !safePayload.documentNumber) {
      safePayload.documentNumber = safePayload.document_number;
    }

    if ('should_use_new_pj_kyc' in safePayload) {
      safePayload.should_use_new_pj_kyc = normalizeBoolean(safePayload.should_use_new_pj_kyc);
    }

    if ('new_pj_kyc_start_date' in safePayload) {
      safePayload.new_pj_kyc_start_date = safePayload.new_pj_kyc_start_date || null;
    }

    [
      'has_pin_defined',
      'hasPinDefined',
      'has_operation_password',
      'hasOperationPassword',
      'operation_password_created',
      'operationPasswordCreated',
      'has_banking_password',
      'hasBankingPassword',
      'banking_password_created',
      'bankingPasswordCreated',
      'has_account_pin',
      'hasAccountPin',
      'account_pin_created',
      'accountPinCreated',
    ].forEach((key) => {
      if (key in safePayload) {
        safePayload[key] = normalizeBoolean(safePayload[key]);
      }
    });

    return safePayload;
  };

  const updateAccountCache = (payload) => {
    setStatusError(null);
    const safePayload = normalizeAccountPayload(payload);
    queryClient.setQueryData(['baas.account'], (oldData = defaultAccount) => ({
      ...oldData,
      ...safePayload,
    }));
  };

  const handleSuccess = (response) => {
    updateAccountCache(response);
    setStatusError(null);
    notify.success('Conta Cakto Banking criada com sucesso. Validaremos o cadastro nominal em instantes.');
  };

  /**
   * Função auxiliar para extrair mensagem de erro como string válida
   * @param {Error} error - Erro da requisição
   * @returns {string} Mensagem de erro como string
   */
  const getErrorMessage = (error) => {
    let message = 
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      error?.toString?.() ||
      'Não foi possível criar a conta agora.';
    
    if (typeof message !== 'string') {
      if (typeof message === 'object') {
        try {
          message = JSON.stringify(message);
        } catch {
          message = 'Não foi possível criar a conta agora.';
        }
      } else {
        message = String(message);
      }
    }
    
    if (message.trim().startsWith('<')) {
      message = 'Erro no servidor. Por favor, tente novamente.';
    }
    
    if (message.length > 200) {
      message = `${message.substring(0, 200)}...`;
    }
    
    return message;
  };

  /**
   * Handler de erro para criação de conta
   * @param {Error} error - Erro da requisição
   */
  const handleError = (error) => {
    const detail = getErrorMessage(error);
    setStatusError(detail);
    notify.error(detail);
  };

  const {
    mutateAsync: createPFAccount,
    isLoading: isCreatingPF,
  } = useMutation({
    mutationFn: createAccountPF,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const {
    mutateAsync: createPJAccount,
    isLoading: isCreatingPJ,
  } = useMutation({
    mutationFn: createAccountPJ,
    onSuccess: handleSuccess,
    onError: handleError,
  });

  const memoedValue = useMemo(
    () => {
      const hasAccountValue = Boolean(
        account?.documentNumber || 
        account?.celcoin_account_id || 
        (account?.status && account?.status !== null)
      );
      
      return {
        account,
        accountResolutionStatus,
        accountResolutionError,
        hasAccount: hasAccountValue,
        isDocumentLocked: Boolean(account?.documentNumber),
        isWithdrawalBlocked: !account?.nominal_withdrawal_registered,
        createPFAccount,
        createPJAccount,
        isCreatingPF,
        isCreatingPJ,
        isLoadingAccount,
        isFetchingAccount,
        refreshAccount,
        accountUpdatedAt,
        statusError: accountResolutionErrorMessage || statusError,
      };
    },
    [
      account,
      accountResolutionStatus,
      accountResolutionError,
      accountResolutionErrorMessage,
      createPFAccount,
      createPJAccount,
      isCreatingPF,
      isCreatingPJ,
      isLoadingAccount,
      isFetchingAccount,
      refreshAccount,
      accountUpdatedAt,
      statusError,
    ]
  );

  return <BaasAccountContext.Provider value={memoedValue}>{children}</BaasAccountContext.Provider>;
};

BaasAccountProvider.propTypes = {
  children: PropTypes.node,
};

BaasAccountProvider.displayName = 'BaasAccountProvider';

export default BaasAccountProvider;
